import { Intent } from '@blueprintjs/core';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import {
  PublishFileUploadResponse,
  publishFileUploads,
} from 'src/actions/projects/action-creators/publishProject/publishFileUploads';
import { publishFileUploadsMetadata } from 'src/actions/projects/action-creators/publishProject/publishFileUploadsMetadata';
import { publishModel } from 'src/actions/projects/action-creators/publishProject/publishModel';
import { publishNamespaces } from 'src/actions/projects/action-creators/publishProject/publishNamespaces';
import { publishRules } from 'src/actions/projects/action-creators/publishProject/publishRules';
import { publishSensitivePropertyGroups } from 'src/actions/projects/action-creators/publishProject/publishSensitivePropertyGroups';
import {
  PublishVirtualGraphResponse,
  publishVirtualGraphs,
} from 'src/actions/projects/action-creators/publishProject/publishVirtualGraphs';
import {
  PublishProjectResponse,
  projectsActionCreators,
} from 'src/actions/projects/actionCreators';
import {
  ConnectionInfo,
  CsvMappingsInfo,
  ModelInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
  VirtualGraphMappingsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { dataSourceTypeDescriptionSelector } from 'src/selectors/dataSourceTypeDescription';
import { PublishHistory } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { exportResources } from 'src/utils/export/resources';
import { PublishFileUpload, PublishVirtualGraph } from 'src/utils/export/types';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';

export const publishProject =
  ({
    projectId,
    publishedDate,
    connectionInfo,
    doExportProject,
    isPublishingRules,
    isPublishingSPG,
    modelInfo,
    csvMappingInfo,
    vgMappingInfo,
    ruleInfo,
    spgInfo,
  }: {
    projectId: string;
    publishedDate: number;
    connectionInfo: ConnectionInfo;
    doExportProject: boolean;
    isPublishingRules: boolean;
    isPublishingSPG: boolean;
    modelInfo: ModelInfo;
    csvMappingInfo?: CsvMappingsInfo;
    vgMappingInfo?: VirtualGraphMappingsInfo;
    ruleInfo: RulesInfo;
    spgInfo: SensitivePropertyGroupsInfo;
  }) =>
  async (
    dispatch: AppDispatch,
    getState: ReduxStateGetter
  ): Promise<boolean> => {
    const { databaseId, namespaceIri, namespacePrefix, shouldAddNamespace } =
      connectionInfo;
    const state = getState();
    const {
      dataSources,
      databases,
      projects,
      connections: { connection },
    } = state;
    const project = projects.projectsById[projectId];
    let database = databases.databasesById?.[databaseId];
    if (!project) {
      return false;
    }

    const { diagram, name } = project;
    if (!diagram) {
      return false;
    }

    const publishHistory: PublishHistory = {
      connectionId: connection?.id || '',
      connectionInfo,
      csvResourceHistoriesById: Object.fromEntries(
        Object.entries(csvMappingInfo || {}).map(
          ([key, { creationType, namedGraphIri }]) => [
            key,
            { creationType, namedGraphIri },
          ]
        )
      ),
      doExportProject,
      isPublishingRules,
      isPublishingSPG,
      modelHistory: {
        modelName: modelInfo.modelName,
        namedGraphIri: modelInfo.namedGraphIri,
      },
      sensitivePropertyGroupsByName: Object.fromEntries(
        Object.entries(spgInfo || {}).map(
          ([key, { actionType, publishName }]) => [
            key,
            { actionType, publishName },
          ]
        )
      ),
      vgResourceHistoriesById: Object.fromEntries(
        Object.entries(vgMappingInfo || {}).map(
          ([key, { virtualGraphName }]) => [key, { virtualGraphName }]
        )
      ),
    };

    dispatch(
      projectsActionCreators.publishProjectStarted({
        id: projectId,
        databaseId,
        publishedDate,
      })
    );

    // create new database (if applicable)
    if (!database) {
      const createDatabaseResponse =
        await databasesStardogRequestDispatchers.createDatabase(databaseId, {
          // for optimal Explorer experience
          search: { enabled: true },

          // for the marketplace
          graph: { aliases: true },

          // for new databases, use the model that we're publishing
          reasoning: { 'schema.graphs': modelInfo.namedGraphIri },
        });

      if (
        createDatabaseResponse.type ===
        DatabasesActionType.CREATE_DATABASE_FAILURE
      ) {
        dispatch(
          projectsActionCreators.publishProjectFinished({
            id: projectId,
            name,
            intent: Intent.DANGER,
            databaseId,
            publishedDate,
            responses: [
              {
                success: false,
                action: createDatabaseResponse,
                actionDescription: copy.notifications.database.createDatabase,
                message: copy.notifications.database.createDatabaseFailure,
              },
            ],
            publishHistory,
          })
        );
        dispatch(
          uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false })
        );

        return false;
      }

      await Promise.all([
        databasesStardogRequestDispatchers.requestDatabaseDetails(databaseId),
        databasesStardogRequestDispatchers.requestDatabaseNamespaces(
          databaseId
        ),
        databasesStardogRequestDispatchers.requestNamedGraphs(databaseId),
      ]);

      database = getState().databases.databasesById?.[databaseId];
    }

    const publishModelResponse = await publishModel({
      database,
      project,
      modelInfo,
      publishedDate,
    });
    const didModelFail = !publishModelResponse.success;

    if (didModelFail) {
      dispatch(
        projectsActionCreators.publishProjectFinished({
          id: projectId,
          name,
          intent: Intent.DANGER,
          databaseId,
          publishedDate,
          responses: [publishModelResponse],
          publishHistory,
        })
      );
      dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
      return false;
    }

    const publishNamespacesResponse = await publishNamespaces(
      database,
      project,
      shouldAddNamespace ? { [namespacePrefix]: namespaceIri } : undefined
    );
    const didNamespacesFail =
      publishNamespacesResponse && !publishNamespacesResponse.success;

    const publishRulesResponse = await publishRules({
      database,
      project,
      modelInfo,
      ruleInfo: isPublishingRules ? ruleInfo : {},
    });
    const didRulesFail = publishRulesResponse && !publishRulesResponse.success;

    const publishSpgsResponse = await publishSensitivePropertyGroups(
      database,
      isPublishingSPG ? spgInfo : {}
    );
    const didSpgsFail = publishSpgsResponse && !publishSpgsResponse.success;

    const { classMappingsByResource, relationshipMappingsByResource } =
      getValidMappingsByResource(diagram.model);
    const typeDescription =
      dataSourceTypeDescriptionSelector(connection)(state);
    const { fileUploadExports, virtualGraphExports } = exportResources({
      project,
      classMappingsByResource,
      relationshipMappingsByResource,
      typeDescription,
      csvMappingInfo,
      vgMappingInfo,
    });

    const validFileUploads = fileUploadExports.filter(
      (data): data is PublishFileUpload => Boolean(data.mappingInfo)
    );

    const validVirtualGraphs = virtualGraphExports.filter(
      (data): data is PublishVirtualGraph => Boolean(data.mappingInfo)
    );

    if (!validFileUploads.length && !validVirtualGraphs.length) {
      if (didRulesFail || didNamespacesFail || didSpgsFail) {
        const responses: PublishProjectResponse[] = [];
        if (didNamespacesFail) {
          responses.push(publishNamespacesResponse);
        }
        if (publishRulesResponse && didRulesFail) {
          responses.push(publishRulesResponse);
        }
        if (publishSpgsResponse && didSpgsFail) {
          responses.push(publishSpgsResponse);
        }
        dispatch(
          projectsActionCreators.publishProjectFinished({
            id: projectId,
            name,
            intent: Intent.WARNING,
            databaseId,
            publishedDate,
            responses,
            publishHistory,
          })
        );
        dispatch(
          uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false })
        );
        return true;
      }

      dispatch(
        projectsActionCreators.publishProjectFinished({
          id: projectId,
          name,
          intent: Intent.SUCCESS,
          databaseId,
          publishedDate,
          responses: [],
          publishHistory,
        })
      );
      return true;
    }

    const fileUploadExportResponses = await publishFileUploads({
      databaseId,
      exports: validFileUploads,
    });

    const virtualGraphExportResponses = await publishVirtualGraphs({
      dataSources,
      databaseId,
      exports: validVirtualGraphs,
    });

    const successfulFileUploads: PublishFileUploadResponse[] = [];
    const failedFileUploads: PublishFileUploadResponse[] = [];
    const successfulVirtualGraphs: PublishVirtualGraphResponse[] = [];
    const failedVirtualGraphs: PublishVirtualGraphResponse[] = [];

    fileUploadExportResponses.forEach((response) => {
      if (response.success) {
        successfulFileUploads.push(response);
      } else {
        failedFileUploads.push(response);
      }
    });

    virtualGraphExportResponses.forEach((response) => {
      if (response.success) {
        successfulVirtualGraphs.push(response);
      } else {
        failedVirtualGraphs.push(response);
      }
    });

    const responses: PublishProjectResponse[] = [
      publishModelResponse,
      publishNamespacesResponse,
      publishRulesResponse,
      publishSpgsResponse,
      ...fileUploadExportResponses,
      ...virtualGraphExportResponses,
    ].filter((res): res is PublishProjectResponse => Boolean(res));

    if (!successfulFileUploads.length && !successfulVirtualGraphs.length) {
      dispatch(
        projectsActionCreators.publishProjectFinished({
          id: projectId,
          name,
          intent: Intent.WARNING,
          databaseId,
          publishedDate,
          responses,
          publishHistory,
        })
      );
      dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
      return false;
    }

    // don't need metadata if there are no file uploads at all, or no successful file uploads
    let didFileUploadsMetadataFail = false;
    let publishFileUploadsMetadataResponse: PublishProjectResponse | null =
      null;
    if (successfulFileUploads.length) {
      publishFileUploadsMetadataResponse = await publishFileUploadsMetadata({
        database,
        exports: successfulFileUploads
          .map(({ id }) => validFileUploads.find((upload) => upload.id === id))
          .filter((fileUpload): fileUpload is PublishFileUpload =>
            Boolean(fileUpload)
          ),
        publishedDate,
      });
      didFileUploadsMetadataFail = !publishFileUploadsMetadataResponse.success;

      responses.push(publishFileUploadsMetadataResponse);
    }

    if (
      didRulesFail ||
      didNamespacesFail ||
      didSpgsFail ||
      didFileUploadsMetadataFail ||
      failedFileUploads.length ||
      failedVirtualGraphs.length
    ) {
      dispatch(
        projectsActionCreators.publishProjectFinished({
          id: projectId,
          name,
          intent: Intent.WARNING,
          databaseId,
          publishedDate,
          responses,
          publishHistory,
        })
      );
      dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: false }));
      return false;
    }

    dispatch(
      projectsActionCreators.publishProjectFinished({
        id: projectId,
        name,
        intent: Intent.SUCCESS,
        databaseId,
        publishedDate,
        responses: [],
        publishHistory,
      })
    );
    return true;
  };
